import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { isAndroidApp } from "@/lib/platform";
import { AdSlot } from "@/components/ads/AdSlot";
import { ADSENSE_SLOT_BANNER } from "@/lib/should-show-ads";

const SUPPORT_EMAIL = "support@fitauraai.com";

const Terms = () => {
  const navigate = useNavigate();
  const onAndroid = isAndroidApp();

  // SEO handled by <SEO /> component below.

  return (
    <>
      <SEO />
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-primary/10 blur-[140px]" />
      </div>
      <header className="sticky top-0 z-30 border-b border-white/5 bg-background/85 px-5 pt-[max(env(safe-area-inset-top),10px)] pb-3 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            aria-label="Back"
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 ring-1 ring-primary/30 shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)]">
            <img src="/app-icon-128.png" alt="FitAura AI" className="h-full w-full object-cover" />
          </span>
          <div className="min-w-0">
            <span className="font-display block text-[15px] font-bold leading-none tracking-tight">
              FitAura<span className="text-primary"> AI</span>
            </span>
            <span className="font-display mt-1 block truncate text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Terms of Service
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl px-5 py-6">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-[0_0_24px_-6px_hsl(var(--primary))]">
            <FileText className="h-6 w-6" />
          </div>
          <div>
            <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
              — TERMS
            </span>
            <h2 className="font-display mt-0.5 text-2xl font-bold uppercase tracking-tight">The rules, in plain English.</h2>
            <p className="text-xs text-muted-foreground">Last updated: April 20, 2026</p>
          </div>
        </div>

        <Card className="space-y-6 p-5 shadow-soft">
          <section>
            <h3 className="mb-2 text-base font-bold">1. Who you're contracting with</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              These Terms are a binding agreement between you and{" "}
              <span className="font-semibold text-foreground">FitAura AI</span> (operated by
              Gayam Venkateswarlu Reddy). By creating an account or using the service, you agree to these
              Terms. If you do not agree, do not use the service.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">2. The service</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              FitAura AI is an AI-powered virtual try-on and outfit styling app. You upload a
              photo of yourself and (optionally) a garment, and our AI generates a visualisation
              of how the garment would look on you. Generations are paid for using credits.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">3. Eligibility &amp; account</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              You must be at least 13 years old (and have legal capacity in your jurisdiction)
              to use FitAura AI. You are responsible for keeping your account credentials safe
              and for all activity that happens under your account. Provide accurate
              information and keep it up to date.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">4. Acceptable use</h3>
            <p className="mb-2 text-sm leading-relaxed text-muted-foreground">
              You agree NOT to use the service to:
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
              <li>Upload photos of any person without their consent.</li>
              <li>Upload photos of minors, sexually explicit imagery, or content depicting violence, hate, or harassment.</li>
              <li>Create deepfakes intended to deceive, defame, or impersonate others.</li>
              <li>Infringe anyone's intellectual property, privacy, or publicity rights.</li>
              <li>Attempt to reverse engineer, scrape, probe, or interfere with the service or its security.</li>
              <li>Resell, redistribute, or wrap the service without our written permission.</li>
              <li>Break any applicable law or regulation.</li>
            </ul>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              We may remove content, restrict outputs, or suspend accounts that violate these rules.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">5. AI-generated content</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Subject to your compliance with these Terms, you own the images you upload and
              you receive a personal, non-exclusive licence to use the AI-generated outputs
              for personal, non-commercial purposes. AI outputs may be inaccurate, may not
              reflect real-world fit, fabric, or sizing, and should not be relied on for
              regulated, professional, or medical decisions. You are responsible for your
              prompts, your inputs, and how you use the outputs — including verifying you
              hold the rights to any product or person depicted.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              We may filter, refuse, or moderate generations that appear to violate these
              Terms or our content policies. If you believe an output infringes your rights,
              contact us at <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a> and we will review.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">6. Credits, payments &amp; billing</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Generations consume credits. You can purchase credit packs through the app.
              Prices, currency and applicable taxes are shown at checkout.
            </p>
            {onAndroid ? (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                On Android, all purchases are processed by{" "}
                <span className="font-semibold text-foreground">Google Play Billing</span>.
                Google Play handles checkout, taxes, invoicing and refund requests according
                to their terms. Manage your purchases or request a refund from your{" "}
                <span className="font-semibold text-foreground">Google Play account</span>.
              </p>
            ) : (
              <>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
                  <li>Customers in India are billed in INR via Razorpay.</li>
                  <li>International customers are billed by our online reseller and Merchant of Record, who handles checkout, tax, invoicing and refunds.</li>
                </ul>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  <span className="font-semibold text-foreground">Reseller notice:</span> Our order process for international web transactions is conducted by our online reseller, who acts as Merchant of Record. They provide all customer service inquiries and handle returns. Payment terms, billing, tax, cancellation and refund mechanics for international web orders are governed by the reseller's buyer terms shown at checkout.
                </p>
              </>
            )}
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Credits are digital goods consumed when you generate. Refund handling is described in our{" "}
              <Link to="/refund" className="underline">Refund Policy</Link>.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">6a. Affiliate &amp; shopping links</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Some links in FitAura AI to retailers such as Amazon, Flipkart, Myntra and Nykaa
              are affiliate links. As an Amazon Associate and partner of these programs, we may
              earn a commission on qualifying purchases at no extra cost to you. Product tiles
              shown in our catalog are <span className="font-semibold text-foreground">AI-generated style references</span>,
              not photographs of specific branded products — tapping a tile takes you to a
              search on the retailer's site so you can find a similar real product. Prices,
              availability and product details are controlled entirely by the retailer.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">6b. AI try-on accuracy</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Virtual try-on results are generated by AI and are intended as
              <span className="font-semibold text-foreground"> visual inspiration only</span>.
              They do not represent how a real garment will fit, drape, feel or fall on you,
              and may differ in colour, fabric texture or proportions from the actual product.
              Always check the retailer's size guide and reviews before purchasing.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">7. Intellectual property</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              FitAura AI and its software, designs, branding, and underlying models are owned
              by us or our licensors. We grant you a limited, non-exclusive, non-transferable,
              revocable right to use the service in accordance with these Terms. All rights
              not expressly granted are reserved.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">8. Service availability &amp; warranty disclaimer</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              The service is provided "as is" and "as available". To the maximum extent
              permitted by law, we disclaim all implied warranties, including merchantability,
              fitness for a particular purpose, and non-infringement. We do not guarantee
              uninterrupted or error-free operation, or that AI outputs will meet your
              expectations.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">9. Limitation of liability</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              To the maximum extent permitted by law, our aggregate liability arising out of
              or relating to the service is limited to the greater of (a) the fees you paid
              us in the 6 months before the event giving rise to the claim, or (b) USD 50.
              We are not liable for any indirect, incidental, special, consequential, or
              punitive damages, including loss of profits, data, or goodwill. Nothing in
              these Terms limits liability for fraud, gross negligence, death, or personal
              injury where that limitation is prohibited by law.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">10. Indemnity</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              You agree to indemnify and hold us harmless from any claims, losses, or expenses
              arising from your uploaded content, your violation of these Terms, or your
              misuse of the service.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">11. Suspension &amp; termination</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              We may suspend or terminate your access for material breach, non-payment,
              security or fraud risk, or repeated policy violations. You may stop using the
              service at any time. Sections that by their nature should survive termination
              (IP, disclaimers, liability, indemnity, governing law) will survive.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">12. Changes to the service or Terms</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              We may update the service and these Terms from time to time. If we make
              material changes, we will update the date above and, where appropriate, notify
              you in the app. Continued use after changes means you accept them.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">13. Governing law</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              These Terms are governed by the laws of India. Disputes will be subject to the
              exclusive jurisdiction of the courts of Bengaluru, Karnataka, except where
              local consumer law gives you the right to bring proceedings in your country
              of residence.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-base font-bold">14. Contact</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Questions about these Terms? Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>.
            </p>
          </section>
        </Card>
        <AdSlot slot={ADSENSE_SLOT_BANNER} format="auto" className="mt-6" />

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="underline-offset-2 hover:underline">Back to FitAura AI</Link>
          {" · "}
          <Link to="/privacy" className="underline-offset-2 hover:underline">Privacy</Link>
          {" · "}
          <Link to="/refund" className="underline-offset-2 hover:underline">Refund Policy</Link>
        </p>
      </main>
    </div>
    </>
  );
};

export default Terms;
